import type { ColumnSizingState, RowData } from "@tanstack/react-table";
import type { DataGridColumnInstance } from "../columns/types";

/**
 * `--dg-cell-px` as resolved on the table root, in px. Undefined until the
 * table's styles apply (and in jsdom), so callers keep the column's plain size.
 */
export const readCellPadding = (root: HTMLElement): number | undefined => {
    if (getComputedStyle(root).getPropertyValue("--dg-cell-px").trim() === "") {
        return undefined;
    }
    const probe = document.createElement("div");
    probe.style.position = "absolute";
    probe.style.visibility = "hidden";
    probe.style.paddingLeft = "var(--dg-cell-px)";
    root.append(probe);
    const padding = Number.parseFloat(getComputedStyle(probe).paddingLeft);
    probe.remove();
    return Number.isFinite(padding) ? padding : undefined;
};

/**
 * Size bounds for `computeColumnWidths`, with `meta.contentWidth` plus padding
 * when set. `meta.defaultMaxSize` caps only columns without a width in
 * `sizing`: a user's width is never cut back.
 */
export const getColumnSizeBounds = <TData extends RowData>({
    column,
    cellPadding,
    sizing,
}: {
    column: DataGridColumnInstance<TData>;
    cellPadding: number | undefined;
    sizing: ColumnSizingState;
}) => {
    const { contentWidth, defaultMaxSize } = column.columnDef.meta ?? {};
    if (contentWidth !== undefined && cellPadding !== undefined) {
        const size = contentWidth + cellPadding * 2;
        return { size, minSize: size, maxSize: size };
    }
    const maxSize = Object.hasOwn(sizing, column.id)
        ? undefined
        : defaultMaxSize;
    return {
        size: column.getSize(),
        minSize: column.columnDef.minSize ?? 0,
        maxSize: maxSize ?? column.columnDef.maxSize ?? Number.MAX_SAFE_INTEGER,
    };
};
